import {
  Button,
  ButtonGroup,
  Checkbox,
  InputGroup,
  Label,
  Spinner,
  Tooltip,
  Position as TTPosition,
  Slider,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import styled from '@emotion/styled';
import { inject } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import moment from 'moment';
import * as React from 'react';
import { Circle } from '@react-google-maps/api';
import { TLCode } from '../../common/enums';
import { generateBasicIcon, generateCustomIcon } from '../../common/icons';
import {
  AuthenticationStore,
  DutyAssignmentStore,
  UiStore,
  SecretariatNeighborStore,
  GroupStore,
  SecretariatStore,
  PharmacyDetailedStore,
} from '../../store';
import { Anchor } from '../../store/models/Anchor';
import { Group } from '../../store/models/Group';
import { BlackPoint } from '../../store/models/BlackPoint';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { DutyOffRequest } from '../../store/models/DutyOffRequest';
import { Pharmacy } from '../../store/models/Pharmacy';
import { PharmacyDetails } from '../../store/models/PharmacyDetails';
import { getUserLocation } from '../../store/utils';
import PharmacySelection from '../common/dropdownSelection/PharmacySelection';
import DateInput from '../common/input/DateInput';
import PharmagardeMap from '../common/map/PharmagardeMap';
import NotificationToast from '../common/NotificationToast';
import PharmacyMarker from '../common/PharmacyMarker';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { PharmacyNeighbor } from '../../store/models';
import memoize from 'memoize-one';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const MapContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0.95;
`;

const Maps = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const DivFlex = styled.div`
  display: flex;
  flex: 1;
`;

const DivColumn1 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 45px;
`;

interface ShowDutiesOnWideMapProps extends WithTranslateProps {
  searchDuties: (form: any) => void;
  searchBlackpoints: (form: any) => void;
  searchDutyOffs: (form: any) => void;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  getPharmacyById: (id: number) => any;
  getPharmacyDetails: (id: number) => any;
  date?: string;
  anchors: Array<Instance<typeof Anchor>>;
  dutySelected?: Instance<typeof DutyAssignment>;
  onDateChange: (date: string) => void;
  isSecretariat?: boolean;
  presetPharmacy?: Instance<typeof PharmacyDetails>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
  SecretariatNeighborStore?: Instance<typeof SecretariatNeighborStore>;
  DutyAssignmentStore?: Instance<typeof DutyAssignmentStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
  UiStore?: Instance<typeof UiStore>;
  GroupStore?: Instance<typeof GroupStore>;
  groupEditMode: boolean;
  planningId: number;
  groupList?: Array<Instance<typeof Group>>;
  minimalDutiesPage?: boolean;
  pharmacyId?: number;
}

interface ShowDutiesOnWideMapState {
  userLocation?: GeolocationPosition;
  requestingUserLocation: boolean;
  loading: boolean;
  initialZoomCalculatedDay: boolean;
  initialZoomCalculatedNight: boolean;
  showPharmacyNotOnDuty: boolean;
  showPharmacyDutyCircle: boolean;
  showPharmacyDuty30kmCircle: boolean;
  showLevel3Duties: boolean;
  showLevel1Duties: boolean;
  showLevel0Duties: boolean;
  showAdjacentDuties: boolean;
  showDayDuties: boolean;
  showNightDuties: boolean;
  showAnchor: boolean;
  searchDate: Date;
  searchPlanning: string;
  blackpoints: Array<Instance<typeof BlackPoint>>;
  dutyOffs: Array<Instance<typeof DutyOffRequest>>;
  dutiesDay: Array<Instance<typeof DutyAssignment>>;
  dutiesNight: Array<Instance<typeof DutyAssignment>>;
  pharmacySelected?: Instance<typeof PharmacyDetails>;
  pharmacyNeighbors?: Array<Instance<typeof PharmacyNeighbor>>;
  groups: any[];
  groupCheckboxesDisabled: boolean;
  pharmacyNeighborsDistanceToShow: number;
  centerCoordinates?: any;
  map: google.maps.Map | undefined;
  pharmacyCenteredCircle: google.maps.Circle | undefined;
}

@inject('DutyAssignmentStore', 'SecretariatNeighborStore', 'GroupStore')
@inject('UiStore', 'AuthenticationStore', 'SecretariatStore', 'PharmacyDetailedStore')
class ShowDutiesOnWideMap extends React.Component<ShowDutiesOnWideMapProps, ShowDutiesOnWideMapState> {
  state = {
    requestingUserLocation: false,
    initialZoomCalculatedDay: false,
    initialZoomCalculatedNight: false,
    loading: false,
    showPharmacyNotOnDuty: true,
    showPharmacyDutyCircle: false,
    showPharmacyDuty30kmCircle: false,
    showLevel3Duties: true,
    showLevel1Duties: true,
    showLevel0Duties: true,
    showAdjacentDuties: true,
    showDayDuties: true,
    showNightDuties: true,
    showAnchor: false,
    searchDate: new Date(),
    searchPlanning: this.props.planningId ? this.props.planningId.toString() : -1,
    dutiesDay: [],
    dutiesNight: [],
    blackpoints: [],
    dutyOffs: [],
    pharmacyList: [],
    groupCheckboxesDisabled: false,
    groups: [],
    pharmacyNeighborsDistanceToShow: 20000,
    map: undefined,
    pharmacyCenteredCircle: undefined,
  } as ShowDutiesOnWideMapState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    const { GroupStore } = this.props;
    try {
      await getUserLocation()
        .then((position) => {
          this._isMounted && this.setState({ requestingUserLocation: false, userLocation: position });
        })
        .catch((err) => {
          const uphocPosition = {
            coords: {
              latitude: 50.41753,
              longitude: 3.908192,
              accuracy: 20,
              altitude: null,
              altitudeAccuracy: null,
              heading: null,
              speed: null,
            },
            timestamp: new Date().getTime(),
          };
          this._isMounted && this.setState({ requestingUserLocation: false, userLocation: uphocPosition });
        });
      this._isMounted && this.setState({ pharmacySelected: this.props.presetPharmacy });
      let date = this.props.date
        ? this.props.date
        : moment(this.state.searchDate)
            .startOf('day')
            .format('YYYY-MM-DD');
      if (this.props.dutySelected != null) {
        const pharmacy = this.props.pharmacyList.find(
          (elem) => elem.pharmacyId === this.props.dutySelected!.pharmacyId,
        );
        const pharmacyDetails = await this.props.getPharmacyDetails(pharmacy!.pharmacyId);
        this._isMounted &&
          this.setState({
            searchDate: new Date(this.props.dutySelected!.duty!.dutyDate!.date),
            pharmacySelected: pharmacyDetails,
          });
        date = moment(this.props.dutySelected!.duty!.dutyDate!.date)
          .startOf('day')
          .format('YYYY-MM-DD');
      } else if (this.props.date != null) {
        this._isMounted && this.setState({ searchDate: new Date(this.props.date) });
        date = this.props.date;
      }
      this.searchDuties({ date, notAssigned: false, planningId: this.state.searchPlanning });
      this.searchBlackpoints({ date, notAssigned: false });
      this.searchDutyOffs({ date, notAssigned: false });
      this.props.SecretariatNeighborStore!.loadList();
      await GroupStore!.loadList();
    } catch (err) {
      console.error(`Error while getting user location: ${(err as any).message}`);
      this._isMounted && this.setState({ requestingUserLocation: false });
    }
    if (this.props.pharmacyId !== undefined) {
      const isPharmacyLoaded = this.props.PharmacyDetailedStore!.getById(this.props.pharmacyId) !== undefined;
      if (!isPharmacyLoaded) await this.props.PharmacyDetailedStore!.loadElement(this.props.pharmacyId)!;
      const pharmacyDetails = this.props.PharmacyDetailedStore!.getByIdOrNew(this.props.pharmacyId);
      this._isMounted && this.setState({ pharmacyNeighbors: pharmacyDetails.pharmacyNeighbors.slice() });
    }
    this._isMounted &&
      this.setState({
        centerCoordinates:
          this.props.pharmacyId !== undefined
            ? this.memoizedFindCenterCoordinate('')
            : this.props.UiStore!.centerCoordinates!,
      });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  searchDuties = async (date) => {
    const result: any = await this.props.searchDuties(date);
    const dutiesDay = result.filter((elem) => elem.duty.dutyType.type === 'DAY');
    const dutiesNight = result.filter((elem) => elem.duty.dutyType.type === 'NIGHT');
    this._isMounted && this.setState({ dutiesDay, dutiesNight });
  };

  searchBlackpoints = async ({ date, notAssigned }) => {
    const formattedDate = moment(date)
      .startOf('day')
      .format('YYYY-MM-DD');
    const blackpoints: any = await this.props.searchBlackpoints({
      date: formattedDate,
      notAssigned,
    });
    this._isMounted && this.setState({ blackpoints });
  };

  searchDutyOffs = async ({ date, notAssigned }) => {
    const formattedDate = moment(date)
      .startOf('day')
      .format('YYYY-MM-DD');
    const dutyOffs: any = await this.props.searchDutyOffs({
      date: formattedDate,
      notAssigned,
    });
    this._isMounted && this.setState({ dutyOffs });
  };

  selectPharmacyById = async (pharmacyId) => {
    this.searchDuties({ date: this.props.date, notAssigned: false, planningId: this.state.searchPlanning });
  };

  selectPharmacy = async (pharmacy) => {
    await this.onPharmacyChange(pharmacy);
  };

  unassignDuty = async (duatyAssignmentId, pharmacyId) => {
    const dayDuty = this.state.dutiesDay.find((dd) => dd.pharmacyId == pharmacyId);
    const nightDuty = this.state.dutiesNight.find((dd) => dd.pharmacyId == pharmacyId);
    await Promise.all([
      dayDuty ? this.props.DutyAssignmentStore!.unassignDuty(dayDuty.dutyAssignmentId) : null,
      nightDuty ? this.props.DutyAssignmentStore!.unassignDuty(nightDuty.dutyAssignmentId) : null,
    ]);
    this.searchDuties({ date: this.props.date, notAssigned: false, planningId: this.state.searchPlanning });
  };

  isDayOnly = (pharmacyId) => {
    return !this.state.dutiesNight.find((dd) => dd.pharmacyId === pharmacyId);
  };

  isDayAndNight = (pharmacyId) => {
    return !!this.state.dutiesNight.find((dd) => dd.pharmacyId === pharmacyId);
  };

  isFree = (pharmacyId) => {
    return (
      !this.state.dutiesNight.find((dd) => dd.pharmacyId === pharmacyId) &&
      !this.state.dutiesDay.find((dd) => dd.pharmacyId === pharmacyId)
    );
  };

  addDayOnly = async (date, pharmacyId) => {
    await this.props.DutyAssignmentStore!.assignDuty({ PharmacyId: pharmacyId, Type: 'DAY', Date: date });
    this.searchDuties({ date: this.props.date, notAssigned: false });
  };

  addDayNight = async (date, pharmacyId) => {
    await this.props.DutyAssignmentStore!.assignDuty({ PharmacyId: pharmacyId, Type: 'DAY', Date: date });
    await this.props.DutyAssignmentStore!.assignDuty({ PharmacyId: pharmacyId, Type: 'NIGHT', Date: date });
    this.searchDuties({ date: this.props.date, notAssigned: false });
  };

  dayNightToDayOnly = async (duatyAssignmentId, pharmacyId) => {
    const nightDuty = this.state.dutiesNight.find((dd) => dd.pharmacyId === pharmacyId); // la garde de nuit éventuelle de la pharmacie courante
    // si cette garde de nuit existe, la déassigner
    (await nightDuty) ? this.props.DutyAssignmentStore!.unassignDuty(nightDuty!.dutyAssignmentId) : null;

    this.searchDuties({ date: this.props.date, notAssigned: false });
  };

  dayOnlyToDayNight = async (duatyAssignmentId, pharmacyId) => {
    const dayDuty = this.state.dutiesDay.find((dd) => dd.pharmacyId == pharmacyId); // la garde de jour seul éventuelle de la pharmacie courante
    // si on est bien sur une garde de jour seul, nightDuty devrait ne pas exister
    // pour transformer une garde de jour seul en jour plus nuit, il faut que le serveur assigne une garde de nuit à cette pharmacie à cette date
    // en plus de la garde de jour seul actuellement assignée

    await this.props.DutyAssignmentStore!.assignDuty({
      PharmacyId: pharmacyId,
      Type: 'NIGHT',
      Date: dayDuty!.duty!.dutyDate!.date,
    });
    this.searchDuties({ date: this.props.date, notAssigned: false });
  };

  findCenterFromCoordinateList(listCoordinate) {
    let top;
    let bottom;
    let left;
    let right;
    listCoordinate.forEach((point) => {
      if (!top || point.lat > top) {
        top = point.lat;
      }
      if (!bottom || point.lat < bottom) {
        bottom = point.lat;
      }
      if (!left || point.lng < left) {
        left = point.lng;
      }
      if (!right || point.lng > right) {
        right = point.lng;
      }
    });
    const lat = (top + bottom) / 2;
    const lng = (left + right) / 2;

    return {
      lat,
      lng,
    };
  }

  findCenterCoordinate = (type: string) => {
    const { pharmacySelected, showPharmacyNotOnDuty, dutiesNight, dutiesDay, userLocation } = this.state;
    const { pharmacyList, pharmacyId } = this.props;
    const pharmacy = pharmacyList.find((ph) => ph.pharmacyId == pharmacyId);
    if (pharmacyId !== undefined && pharmacy !== undefined && pharmacy.latitude && pharmacy.longitude)
      return { lat: pharmacy.latitude, lng: pharmacy.longitude };
    if (pharmacySelected != null && pharmacySelected.latitude && pharmacySelected.longitude) {
      return { lat: pharmacySelected.latitude, lng: pharmacySelected.longitude };
    } else if (showPharmacyNotOnDuty && pharmacyList.filter((ph) => ph.latitude && ph.longitude).length !== 0) {
      return this.findCenterFromCoordinateList(
        pharmacyList
          .filter((ph) => ph.latitude && ph.longitude)
          .map((ph) => {
            return { lat: ph.latitude, lng: ph.longitude };
          }),
      );
    } else if (type === 'DAY' && dutiesDay.length !== 0) {
      return this.findCenterFromCoordinateList(
        dutiesDay
          .filter((d) => d !== undefined && d.pharmacySimple && d.pharmacySimple.latitude && d.pharmacySimple.longitude)
          .map((d) => {
            return { lat: d.pharmacySimple!.latitude, lng: d.pharmacySimple!.longitude };
          }),
      );
    } else if (type === 'NIGHT' && dutiesNight.length !== 0) {
      return this.findCenterFromCoordinateList(
        dutiesNight
          .filter((d) => d !== undefined && d.pharmacySimple && d.pharmacySimple.latitude && d.pharmacySimple.longitude)
          .map((d) => {
            return { lat: d.pharmacySimple!.latitude, lng: d.pharmacySimple!.longitude };
          }),
      );
    } else if (userLocation) {
      return {
        lat: userLocation.coords.latitude,
        lng: userLocation.coords.longitude,
      };
    } else {
      return undefined;
    }
  };

  memoizedFindCenterCoordinate = memoize(this.findCenterCoordinate);

  renderPharmacyFocusedMarker = (pharmacyId: number) => {
    const pharmacy = this.props.pharmacyList.find((ph) => ph.pharmacyId === pharmacyId)!;

    const { dutiesDay, dutiesNight } = this.state;

    const nightAndDayDuties = dutiesNight.filter((d) =>
      dutiesDay.find((n) => n.pharmacyId === d.pharmacyId && n.duty!.dutyDate.date === d.duty!.dutyDate.date),
    );
    const dayOnlyDuties = dutiesDay.filter(
      (d) => !dutiesNight.find((n) => n.pharmacyId === d.pharmacyId && n.duty!.dutyDate.date === d.duty!.dutyDate.date),
    );

    let upColor = '888888';
    let downColor = '888888';
    let iconSize = 34;

    if (nightAndDayDuties.find((d) => d.pharmacyId === pharmacy!.pharmacyId)) {
      upColor = 'FFCA18';
    } else if (dayOnlyDuties.find((d) => d.pharmacyId === pharmacy!.pharmacyId)) {
      downColor = 'FFCA18';
      upColor = 'FFCA18';
    } else {
      downColor = '29B6F6';
      upColor = '29B6F6';
      iconSize = 24;
    }

    const icon = generateCustomIcon(iconSize, iconSize, upColor, downColor, true, '000000', false, '888888', '1', '1');

    return (
      <PharmacyMarker
        key={'pharmacyId_' + pharmacyId}
        id={pharmacyId}
        marker={pharmacy}
        zIndex={102}
        icon={icon}
        onSelectionClick={this.selectPharmacy}
        simpleCalendarStartYear={this.state.searchDate.getFullYear()}
      />
    );
  };

  renderPharmacyCircleMarker = () => {
    const pharmacy = this.props.pharmacyList.find((ph) => ph.pharmacyId === this.props.pharmacyId)!;

    if (
      this.state.pharmacyCenteredCircle === undefined ||
      this.state.pharmacyNeighborsDistanceToShow !== this.state.pharmacyCenteredCircle.getRadius()
    ) {
      const newCircle = new google.maps.Circle({
        radius: this.state.pharmacyNeighborsDistanceToShow,
        center: { lat: Number(pharmacy.latitude), lng: Number(pharmacy.longitude) },
      });

      this.setState({ pharmacyCenteredCircle: newCircle });
      if (this.state.map && newCircle.getBounds()) {
        this.state.map.fitBounds(newCircle.getBounds()!);
      }
    }

    return (
      <Circle
        key={pharmacy.pharmacyId + '_NeighborCircle'}
        center={{ lat: Number(pharmacy.latitude), lng: Number(pharmacy.longitude) }}
        radius={this.state.pharmacyNeighborsDistanceToShow}
        options={{
          fillColor: '#A9CCE3',
          fillOpacity: 0.2,
          strokeWeight: 0.5,
        }}
      />
    );
  };

  renderPharmacyNeighborsMarker = () => {
    if (this.state.pharmacyNeighbors === undefined) return [];

    const { dutiesDay, dutiesNight } = this.state;

    const nightAndDayDuties = dutiesNight.filter((d) =>
      dutiesDay.find((n) => n.pharmacyId === d.pharmacyId && n.duty!.dutyDate.date === d.duty!.dutyDate.date),
    );
    const dayOnlyDuties = dutiesDay.filter(
      (d) => !dutiesNight.find((n) => n.pharmacyId === d.pharmacyId && n.duty!.dutyDate.date === d.duty!.dutyDate.date),
    );

    return this.state.pharmacyNeighbors
      .filter((pn) => pn.distance !== undefined && pn.distance <= this.state.pharmacyNeighborsDistanceToShow)
      .filter(
        (pn) =>
          nightAndDayDuties.find((d) => d.pharmacyId === pn.neighborId) ||
          dayOnlyDuties.find((d) => d.pharmacyId === pn.neighborId),
      )
      .map((pn) => {
        var neighbor = this.props.pharmacyList.find((p) => p.pharmacyId === pn.neighborId);

        if (neighbor === undefined) return;

        let upColor = '888888';
        let downColor = '888888';
        let zIndex = 100;

        if (nightAndDayDuties.find((d) => d.pharmacyId === neighbor!.pharmacyId)) {
          upColor = 'FFCA18';
          zIndex = 101;
        }
        if (dayOnlyDuties.find((d) => d.pharmacyId === neighbor!.pharmacyId)) {
          downColor = 'FFCA18';
          upColor = 'FFCA18';
          zIndex = 101;
        }

        const icon = generateCustomIcon(24, 24, upColor, downColor, false, '000000', false, '888888', '1', '1');

        return (
          <PharmacyMarker
            key={'Neighbor_pharmacyId_' + neighbor.pharmacyId}
            id={neighbor.pharmacyId}
            marker={neighbor}
            zIndex={zIndex}
            icon={icon}
            simpleCalendarStartYear={this.state.searchDate.getFullYear()}
          />
        );
      });
  };

  renderPharmacyDutiesMarkers = (duties: Array<Instance<typeof DutyAssignment>>, type: string) => {
    const { pharmacySelected, showLevel3Duties, showLevel1Duties, showLevel0Duties } = this.state;
    const { groupList } = this.props;
    const markers: any[] = [];
    duties
      .filter((d) => d.pharmacyId)
      .forEach(async (d, index) => {
        const pharmacies = this.props.pharmacyList.filter((ph) => ph.pharmacyId === d.pharmacyId!);
        if (pharmacies.length === 0) {
          // might happen if one of the pharmacies with a duty that day is not active (error in data)
          NotificationToast.showError(
            'Data error: pharmacy [' + d.pharmacyId + '] could not be displayed (check active flag?)',
          );
        } else {
          if (!showLevel3Duties && pharmacies[0]!.level === 3) return;
          if (!showLevel1Duties && pharmacies[0]!.level === 1) return;
          if (!showLevel0Duties && pharmacies[0]!.level === 0) return;

          const isLvl0 = pharmacies[0]!.level === 0;
          const size = isLvl0 ? 22 : 32;

          const grp = groupList!.find((grp) => grp.groupId === pharmacies[0]!.groupId);
          let upColor = grp ? grp.color : 'FFCA18';

          upColor = upColor === undefined ? 'FFCA18' : upColor;
          upColor = type === 'NIGHTONLY' ? '888888' : upColor;
          const downColor = d.duty!.dutyType!.type === 'NIGHT' ? '888888' : upColor;
          let icon = generateCustomIcon(
            size,
            size,
            upColor,
            downColor,
            pharmacies[0]!.level === 3,
            '000000',
            isLvl0,
            '888888',
            '1',
            '1',
          );
          if (pharmacySelected != null) {
            const neighborhood = pharmacySelected!.pharmacyNeighbors.find((elem) => {
              return elem.neighborId === d.pharmacyId;
            });
            if (pharmacySelected.pharmacyId === d.pharmacyId) {
              icon = generateBasicIcon(pharmacies[0]!.level, d.duty!.dutyType!.type === 'NIGHT', true, false, false);
            } else if (neighborhood !== undefined && neighborhood!.distance != null) {
              const neighbor = this.props.pharmacyList.find((ph) => ph.pharmacyId === neighborhood.neighborId);
              if (type === 'DAY' && pharmacySelected!.minDistance != null && pharmacySelected!.maxDistance != null) {
                if (neighborhood!.distance < pharmacySelected!.minDistance) {
                  const neighborDuty = duties.find((d1) => d1.pharmacyId === neighborhood.neighborId);
                  icon = generateBasicIcon(
                    neighbor!.level,
                    neighborDuty!.duty!.dutyType!.type === 'NIGHT',
                    false,
                    false,
                    true,
                  );
                }
              } else if (
                type === 'NIGHT' &&
                pharmacySelected!.minDistance2 != null &&
                pharmacySelected!.maxDistance2 != null
              ) {
                if (neighborhood!.distance < pharmacySelected!.minDistance2) {
                  const neighborDuty = duties.find((d1) => d1.pharmacyId === neighborhood.neighborId);
                  icon = generateBasicIcon(
                    neighbor!.level,
                    neighborDuty!.duty!.dutyType!.type === 'NIGHT',
                    false,
                    false,
                    true,
                  );
                }
              }
            }
          }
          markers.push(
            <PharmacyMarker
              key={d.dutyAssignmentId}
              id={d.dutyAssignmentId}
              marker={pharmacies[0]!}
              zIndex={100}
              icon={icon}
              onSelectionClick={this.selectPharmacy}
              onUnassignClick={this.unassignDuty}
              onChangeDayNightToDayOnlyClick={this.dayNightToDayOnly}
              onChangeDayOnlyToDayNightClick={this.dayOnlyToDayNight}
              isDayOnly={this.isDayOnly}
              isDayAndNight={this.isDayAndNight}
              onCalendarChanged={this.selectPharmacyById}
              groupEditMode={this.props.groupEditMode}
              group={
                pharmacies[0]!.groupId === undefined
                  ? undefined
                  : this.props.GroupStore!.dataList.find((g) => g.groupId === pharmacies[0]!.groupId)
              }
              simpleCalendarStartYear={this.state.searchDate.getFullYear()}
            />,
          );
        }
        if (this.state.showPharmacyDutyCircle) {
          const pharmacyDetails = await this.props.getPharmacyDetails(d.pharmacyId!);
          markers.push(
            <Circle
              key={d.pharmacySimple!.pharmacyId + 'Day'}
              center={{ lat: Number(d.pharmacySimple!.latitude), lng: Number(d.pharmacySimple!.longitude) }}
              radius={pharmacyDetails.minDistance}
              options={{
                fillColor: '#A9CCE3',
                fillOpacity: 0.1,
                strokeWeight: 0.5,
              }}
            />,
          );
          markers.push(
            <Circle
              key={d.pharmacySimple!.pharmacyId + 'Night'}
              center={{ lat: Number(d.pharmacySimple!.latitude), lng: Number(d.pharmacySimple!.longitude) }}
              radius={pharmacyDetails.minDistance2}
              options={{
                fillColor: '#A9CCE3',
                fillOpacity: 0.1,
                strokeWeight: 0.5,
              }}
            />,
          );
        }
        if (this.state.showPharmacyDuty30kmCircle && type === 'DAY') {
          markers.push(
            <Circle
              key={d.pharmacySimple!.pharmacyId + '15km'}
              center={{ lat: Number(d.pharmacySimple!.latitude), lng: Number(d.pharmacySimple!.longitude) }}
              radius={15000}
              options={{
                fillColor: '#A9CCE3',
                fillOpacity: 0.1,
                strokeWeight: 0.5,
              }}
            />,
          );
        }
        if (this.state.showPharmacyDuty30kmCircle && type === 'NIGHT') {
          markers.push(
            <Circle
              key={d.pharmacySimple!.pharmacyId + '20km'}
              center={{ lat: Number(d.pharmacySimple!.latitude), lng: Number(d.pharmacySimple!.longitude) }}
              radius={20000}
              options={{
                fillColor: '#A9CCE3',
                fillOpacity: 0.1,
                strokeWeight: 0.5,
              }}
            />,
          );
        }
        if (this.state.showPharmacyDutyCircle) {
          const pharmacyDetails = await this.props.getPharmacyDetails(d.pharmacyId!);
          markers.push(this.renderCircleForPharmacy(pharmacyDetails, 'DAY'));
          markers.push(this.renderCircleForPharmacy(pharmacyDetails, 'NIGHT'));
        }
      });
    return markers;
  };

  renderPharmacyMarkers = (duties: Array<Instance<typeof DutyAssignment>>, type: string) => {
    const { pharmacySelected, showPharmacyNotOnDuty, groups, blackpoints, dutyOffs } = this.state;
    const { groupList } = this.props;
    const date = duties && duties[0] ? (duties[0].duty ? duties[0].duty!.dutyDate.date : null) : null;
    const secretariatId = this.props.AuthenticationStore!.isSecretariat
      ? this.props.AuthenticationStore!.secretariatId!
      : 1;

    const pharmaciesOnBlackpoint = date
      ? this.props.pharmacyList.filter((elem) => {
          return (
            blackpoints.find((b) => b.pharmacyId === elem.pharmacyId && b.startDate <= date! && date! <= b.endDate) !==
            undefined
          );
        })
      : [];
    const pharmaciesOffDuty = this.props.pharmacyList.filter((elem) => {
      return dutyOffs.find((b) => b.pharmacyId === elem.pharmacyId) !== undefined;
    });
    const pharmacyNotOnDuty = this.props.pharmacyList.filter((elem) => {
      return duties.find((d) => d.duty!.dutyType!.type === type && d.pharmacyId === elem.pharmacyId) === undefined;
    });
    const pharmacySelectedNotOnDuty =
      pharmacySelected &&
      pharmacyNotOnDuty.find((elem) => elem.pharmacyId === pharmacySelected.pharmacyId) !== undefined;

    // ph not on duty but unavailable
    const result1 = pharmacyNotOnDuty
      .filter(
        (ph) =>
          ph.latitude &&
          ph.longitude &&
          groups.filter(
            (grp) =>
              grp.value === (ph.groupId === undefined ? '' : groupList!.find((g) => g.groupId === ph.groupId)!.name) ||
              (grp.value === 'zero' && ph.level === 0) ||
              (grp.value === 'one' && ph.secretariatId === 1 && ph.level === 1) ||
              (grp.value === 'other' && ph.level > 0 && ph.secretariatId !== 1),
          ).length > 0 &&
          (pharmaciesOnBlackpoint.find((elem) => ph.pharmacyId === elem.pharmacyId) != undefined ||
            pharmaciesOffDuty.find((elem) => ph.pharmacyId === elem.pharmacyId) != undefined),
      )
      .map((ph) => {
        const isLvl0 = ph.level === 0;
        const size = 16;
        const icon = generateCustomIcon(
          size,
          size,
          'ff0000',
          'ff0000',
          ph.level === 3,
          '000000',
          isLvl0,
          '888888',
          '1',
          '1',
        );
        return (
          <PharmacyMarker
            key={ph.pharmacyId}
            id={ph.apbNumber}
            marker={ph}
            icon={icon}
            onSelectionClick={this.selectPharmacy}
            onAddDayOnlyClick={this.addDayOnly}
            onAddDayNightClick={this.addDayNight}
            isFree={this.isFree}
            date={duties[0].duty!.dutyDate.date}
            groupEditMode={this.props.groupEditMode}
            group={
              ph.groupId === undefined
                ? undefined
                : this.props.GroupStore!.dataList.find((g) => g.groupId === ph.groupId)
            }
            simpleCalendarStartYear={this.state.searchDate.getFullYear()}
          />
        );
      });

    // ph not on duty && selected
    const result2 = pharmacyNotOnDuty
      .filter(
        (ph) =>
          ph.latitude && ph.longitude && pharmacySelectedNotOnDuty && ph.pharmacyId === pharmacySelected!.pharmacyId,
      )
      .map((ph) => {
        const isLvl0 = ph.level === 0;
        const size = 16;
        const icon = generateCustomIcon(
          size,
          size,
          'e9ff00',
          'e9ff00',
          ph.level === 3,
          '000000',
          isLvl0,
          '888888',
          '1',
          '1',
        );
        return (
          <PharmacyMarker
            key={ph.pharmacyId}
            id={ph.apbNumber}
            marker={ph}
            icon={icon}
            onSelectionClick={this.selectPharmacy}
            onAddDayOnlyClick={this.addDayOnly}
            onAddDayNightClick={this.addDayNight}
            isFree={this.isFree}
            date={duties[0].duty!.dutyDate.date}
            groupEditMode={this.props.groupEditMode}
            group={
              ph.groupId === undefined
                ? undefined
                : this.props.GroupStore!.dataList.find((g) => g.groupId === ph.groupId)
            }
            simpleCalendarStartYear={this.state.searchDate.getFullYear()}
          />
        );
      });

    // ph not on duty & available
    return [
      pharmacyNotOnDuty
        .filter(
          (ph) =>
            ph.latitude &&
            ph.longitude &&
            (showPharmacyNotOnDuty || (pharmacySelectedNotOnDuty && ph.pharmacyId === pharmacySelected!.pharmacyId)) &&
            groups.filter(
              (grp) =>
                grp.value ===
                  (ph.groupId === undefined ? '' : groupList!.find((g) => g.groupId === ph.groupId)!.name) ||
                (grp.value === 'zero' && ph.level === 0) ||
                (grp.value === 'close' && this.isNearby(ph.pharmacyId, secretariatId)) ||
                (grp.value === 'one' && ph.secretariatId === secretariatId && ph.level === 1) ||
                (grp.value === 'other' && ph.level > 0 && ph.secretariatId !== secretariatId),
            ).length > 0 &&
            pharmaciesOnBlackpoint.find((elem) => ph.pharmacyId === elem.pharmacyId) === undefined &&
            pharmaciesOffDuty.find((elem) => ph.pharmacyId === elem.pharmacyId) === undefined,
        )
        .map((ph) => {
          const isLvl0 = ph.level === 0;
          const size = 16; // isLvl0?20:32
          const grp = groupList!.find((grp) => grp.groupId === ph.groupId);
          let upColor = grp !== undefined ? grp.color : 'FFCA18';

          upColor = upColor === undefined ? 'FFCA18' : upColor;
          const bottomColor = upColor; // '888888'
          const icon = generateCustomIcon(
            size,
            size,
            upColor,
            bottomColor,
            ph.level === 3,
            '000000',
            isLvl0,
            '888888',
            '0.5',
            '0.5',
          );
          // }
          return (
            <PharmacyMarker
              key={ph.pharmacyId}
              id={ph.apbNumber}
              marker={ph}
              icon={icon}
              onSelectionClick={this.selectPharmacy}
              onAddDayOnlyClick={this.addDayOnly}
              onAddDayNightClick={this.addDayNight}
              isFree={this.isFree}
              date={duties[0].duty!.dutyDate.date}
              groupEditMode={this.props.groupEditMode}
              group={
                ph.groupId === undefined
                  ? undefined
                  : this.props.GroupStore!.dataList.find((g) => g.groupId === ph.groupId)
              }
              simpleCalendarStartYear={this.state.searchDate.getFullYear()}
            />
          );
        }),
      result1,
      result2,
    ];
  };

  isNearby = (pharmacyId: number, secretariatId: number) => {
    var sn = this.props.SecretariatNeighborStore!.dataList.find(
      (sn) => sn.secretariatId === secretariatId && sn.pharmacyId === pharmacyId,
    );
    return sn != undefined;
  };

  reduceBy = (bounds: any, percentage: number) => {
    // @ts-ignore
    const distance: number = window.google.maps.geometry.spherical.computeDistanceBetween(
      bounds.getNorthEast(),
      bounds.getSouthWest(),
    );
    const reduced: number = distance * percentage;

    // @ts-ignore
    const headingNESW: number = window.google.maps.geometry.spherical.computeHeading(
      bounds.getNorthEast(),
      bounds.getSouthWest(),
    );
    // @ts-ignore
    const newNE: any = window.google.maps.geometry.spherical.computeOffset(bounds.getNorthEast(), reduced, headingNESW);

    // @ts-ignore
    const headingSWNE: number = window.google.maps.geometry.spherical.computeHeading(
      bounds.getSouthWest(),
      bounds.getNorthEast(),
    );
    // @ts-ignore
    const newSW: any = window.google.maps.geometry.spherical.computeOffset(bounds.getSouthWest(), reduced, headingSWNE);

    // @ts-ignore
    return new window.google.maps.LatLngBounds(newSW, newNE);
  };

  fitBoundsDay = (map: google.maps.Map) => {
    if (this.state.initialZoomCalculatedDay || this.props.pharmacyList.length === 0) return;
    // @ts-ignore
    const bounds = new window.google.maps.LatLngBounds();
    this.props.pharmacyList.forEach((d) =>
      // @ts-ignore
      bounds.extend(new window.google.maps.LatLng(d.latitude, d.longitude)),
    );

    map.fitBounds(bounds);

    this.setState({ initialZoomCalculatedDay: true });
  };

  fitBoundsStatic = () => {
    if (this.state.initialZoomCalculatedDay || this.props.pharmacyList.length === 0) return;
    this.setState({ initialZoomCalculatedDay: true });
  };

  fitBoundsNight = (map: google.maps.Map) => {
    if (this.state.initialZoomCalculatedNight || this.props.pharmacyList.length === 0) return;
    // @ts-ignore
    const bounds = new window.google.maps.LatLngBounds();
    this.props.pharmacyList.forEach((d) =>
      // @ts-ignore
      bounds.extend(new window.google.maps.LatLng(d.latitude, d.longitude)),
    );
    map.fitBounds(bounds);
    this.setState({ initialZoomCalculatedNight: true });
  };

  dateSearchChange = (date) => {
    this.setState({ searchDate: date });
    const formattedDate = moment(date)
      .startOf('day')
      .format('YYYY-MM-DD');
    this.searchDuties({
      date: formattedDate,
      notAssigned: false,
      planningId: this.state.searchPlanning,
    });
    this.searchBlackpoints({
      date: formattedDate,
      notAssigned: false,
    });
    this.searchDutyOffs({
      date: formattedDate,
      notAssigned: false,
    });

    this.props.onDateChange(formattedDate);
  };

  renderCircleForPharmacy = (pharmacy, type) => {
    return (
      <Circle
        key={pharmacy.pharmacyId + (type === 'DAY' ? 'max1' : 'max2')}
        center={{ lat: pharmacy.latitude, lng: pharmacy.longitude }}
        radius={type === 'DAY' ? pharmacy.minDistance : pharmacy.minDistance2}
        options={{
          fillColor: '#106BA3',
          strokeWeight: 0.5,
        }}
      />
    );
  };

  renderAnchors = () => {
    const markers: any[] = [];
    if (this.state.showAnchor) {
      this.props.anchors.map((elem) => {
        markers.push(
          <Circle
            key={elem.anchorId + 'Circle'}
            center={{ lat: Number(elem.latitude), lng: Number(elem.longitude) }}
            radius={elem.radius}
            options={{
              fillColor: '#10a259',
              strokeWeight: 0.5,
            }}
          />,
        );
      });
    }
    return markers;
  };

  renderMarker = (
    dayDuties,
    nightDuties,
    type,
    groups,
    pharmacySelected,
    showDayDuties,
    showNightDuties,
    showPharmacyDutyCircle,
    // Do not remove those properties.
    showPharmacyDuty30kmCircle,
    showAnchor,
    pharmacyNeighborsDistanceToShow,
  ) => {
    //console.time('RenderingMarkers');
    const { pharmacyId } = this.props;

    if (pharmacyId) {
      return [
        ...this.renderPharmacyNeighborsMarker(),
        this.renderPharmacyFocusedMarker(pharmacyId),
        this.renderPharmacyCircleMarker(),
      ];
    }

    const pharmacies = this.renderPharmacyMarkers(dayDuties, type);
    const nightAndDayDuties = nightDuties.filter((d) =>
      dayDuties.find((n) => n.pharmacyId === d.pharmacyId && n.duty.dutyDate.date === d.duty.dutyDate.date),
    );
    const dayOnlyDuties = dayDuties.filter(
      (d) => !nightDuties.find((n) => n.pharmacyId === d.pharmacyId && n.duty.dutyDate.date === d.duty.dutyDate.date),
    );
    const nightOnlyDuties = nightDuties.filter(
      (d) => !dayDuties.find((n) => n.pharmacyId === d.pharmacyId && n.duty.dutyDate.date === d.duty.dutyDate.date),
    );
    const dayDutyMarkers = showDayDuties ? this.renderPharmacyDutiesMarkers(dayOnlyDuties, 'DAY') : [];
    const nightDutyMarkers = showNightDuties ? this.renderPharmacyDutiesMarkers(nightAndDayDuties, 'NIGHT') : [];
    const nigthOnlyDutyMarkers = showNightDuties ? this.renderPharmacyDutiesMarkers(nightOnlyDuties, 'NIGHTONLY') : [];
    const pharmacyCircleD =
      pharmacySelected && showPharmacyDutyCircle ? this.renderCircleForPharmacy(pharmacySelected, 'DAY') : [];
    const pharmacyCircleN =
      pharmacySelected && showPharmacyDutyCircle ? this.renderCircleForPharmacy(pharmacySelected, 'NIGHT') : [];
    const anchors = this.renderAnchors();

    const result = [
      ...pharmacies,
      ...dayDutyMarkers,
      ...nightDutyMarkers,
      pharmacyCircleD,
      pharmacyCircleN,
      ...anchors,
      ...nigthOnlyDutyMarkers,
    ];

    //console.timeEnd('RenderingMarkers');
    console.timeEnd('otherDayClicked');
    return result;
  };

  memoizedRenderMarker = memoize(this.renderMarker);

  onPharmacyChange = async (pharmacy: Instance<typeof Pharmacy>) => {
    if (pharmacy.pharmacyId != null) {
      const details = await this.props.getPharmacyDetails(pharmacy.pharmacyId);
      this._isMounted && this.setState({ pharmacySelected: details });
    } else {
      this._isMounted && this.setState({ pharmacySelected: undefined });
    }
  };

  handleGroupToggle = (grp) => {
    let newgroups;
    if (this.state.groups.filter((group) => group.value == grp).length > 0) {
      newgroups = this.state.groups.filter((group) => group.value != grp);
    } else {
      newgroups = [...this.state.groups];
      newgroups.push({ label: grp, value: grp });
    }
    this.setState({ groups: newgroups });
  };

  getSecretariatId = () => {
    if (this.props.AuthenticationStore!.isSuperAdmin)
      return this.props.SecretariatStore!.selectedSecretariatId === 0
        ? undefined
        : this.props.SecretariatStore!.selectedSecretariatId;
    if (this.props.AuthenticationStore!.isSecretariat) return this.props.AuthenticationStore!.secretariatId;
    return undefined;
  };
  onUpdateSlider = (value) => {
    this.setState({ pharmacyNeighborsDistanceToShow: value * 1000 });
  };

  render() {
    const {
      loading,
      requestingUserLocation,
      dutiesDay,
      dutiesNight,
      searchDate,
      pharmacySelected,
      initialZoomCalculatedDay,
      pharmacyNeighborsDistanceToShow,
    } = this.state;
    const { translate, pharmacyList, getPharmacyById, isSecretariat } = this.props;
    const { centerCoordinates } = this.state;
    const filteredDayDutyCount = dutiesDay.filter(
      (d) => d !== undefined && d.pharmacySimple !== undefined && d.pharmacySimple!.level > 0,
    ).length;
    const filteredNightDutyCount = dutiesNight.filter(
      (d) => d !== undefined && d.pharmacySimple !== undefined && d.pharmacySimple!.level > 0,
    ).length;
    const filteredLvl1DayDutyCount = dutiesDay.filter(
      (d) => d !== undefined && d.pharmacySimple !== undefined && d.pharmacySimple!.level == 1,
    ).length;
    const filteredLvl3DayDutyCount = dutiesDay.filter(
      (d) => d !== undefined && d.pharmacySimple !== undefined && d.pharmacySimple!.level == 3,
    ).length;
    const filteredLvl1NightDutyCount = dutiesNight.filter(
      (d) => d !== undefined && d.pharmacySimple !== undefined && d.pharmacySimple!.level == 1,
    ).length;
    const filteredLvl3NightDutyCount = dutiesNight.filter(
      (d) => d !== undefined && d.pharmacySimple !== undefined && d.pharmacySimple!.level == 3,
    ).length;
    const {
      showDayDuties,
      showNightDuties,
      showPharmacyDutyCircle,
      showPharmacyDuty30kmCircle,
      showAnchor,
    } = this.state;

    return loading || requestingUserLocation ? (
      <Spinner />
    ) : (
      <Container>
        <SearchContainer>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Label style={{ marginRight: '10px' }}>{translate(TLCode.DUTYASSIGNMENT_Date)}</Label>
            <Tooltip content={translate(TLCode.GENERAL_PreviousDay)} hoverOpenDelay={700}>
              <Button
                icon={IconNames.CHEVRON_LEFT}
                small={true}
                style={{ height: '30px', marginRight: '5px' }}
                onClick={() => {
                  console.time('otherDayClicked');
                  const date = moment(this.state.searchDate)
                    .startOf('day')
                    .subtract(1, 'd');
                  this.dateSearchChange(date.toDate());
                }}
              />
            </Tooltip>
            <DateInput
              value={searchDate}
              canClearSelection={false}
              onChange={(value) => {
                this.dateSearchChange(value);
              }}
            />
            <Tooltip content={translate(TLCode.GENERAL_NextDay)} hoverOpenDelay={700}>
              <Button
                icon={IconNames.CHEVRON_RIGHT}
                small={true}
                style={{ height: '30px', marginLeft: '5px' }}
                onClick={() => {
                  console.time('otherDayClicked');
                  const date = moment(this.state.searchDate)
                    .startOf('day')
                    .add(1, 'd');
                  this.dateSearchChange(date.toDate());
                }}
              />
            </Tooltip>
            <Tooltip content={translate(TLCode.GENERAL_Today)} hoverOpenDelay={700}>
              <Button
                icon={IconNames.DOUBLE_CHEVRON_DOWN}
                small={true}
                style={{ height: '30px', marginLeft: '5px' }}
                onClick={() => {
                  const date = moment(new Date()).startOf('day');
                  this.dateSearchChange(date.toDate());
                }}
              />
            </Tooltip>
            <Tooltip content={translate(TLCode.GENERAL_NextYear)} hoverOpenDelay={700}>
              <Button
                icon={IconNames.DOUBLE_CHEVRON_RIGHT}
                small={true}
                style={{ height: '30px', marginLeft: '5px' }}
                onClick={() => {
                  const date = moment(this.state.searchDate)
                    .startOf('day')
                    .add(1, 'y');
                  date.date(1);
                  date.month(0);
                  this.dateSearchChange(date.toDate());
                }}
              />
            </Tooltip>
            {isSecretariat && !this.props.minimalDutiesPage && (
              <>
                &nbsp;
                <ButtonGroup>
                  <Button
                    text={translate(TLCode.DUTIES_ShowPharmaciesDutyCircle)}
                    onClick={() => this.setState({ showPharmacyDutyCircle: !this.state.showPharmacyDutyCircle })}
                    active={this.state.showPharmacyDutyCircle}
                  />
                  <Button
                    text={translate(TLCode.DUTIES_ShowPharmaciesDuty30KmCircle)}
                    onClick={() =>
                      this.setState({ showPharmacyDuty30kmCircle: !this.state.showPharmacyDuty30kmCircle })
                    }
                    active={this.state.showPharmacyDuty30kmCircle}
                  />
                  <Button
                    text={translate(TLCode.DUTIES_ShowAnchors)}
                    onClick={() => this.setState({ showAnchor: !this.state.showAnchor })}
                    active={this.state.showAnchor}
                  />
                  <Button
                    text={translate(TLCode.DUTIES_ShowDayDuties)}
                    onClick={() => this.setState({ showDayDuties: !this.state.showDayDuties })}
                    active={this.state.showDayDuties}
                  />
                  <Button
                    text={translate(TLCode.DUTIES_ShowNightDuties)}
                    onClick={() => this.setState({ showNightDuties: !this.state.showNightDuties })}
                    active={this.state.showNightDuties}
                  />
                </ButtonGroup>
                &nbsp;
                {/* <PharmacySelection
                  pharmacyList={pharmacyList}
                  pharmacySelected={pharmacySelected}
                  getPharmacyById={getPharmacyById}
                  onChange={this.onPharmacyChange}
                  showDetails={false}
                  allowEmpty={true}
                /> */}
                <PharmacySelection
                  pharmacyList={pharmacyList}
                  onChange={this.onPharmacyChange}
                  pharmacySelected={pharmacySelected}
                  getPharmacyById={getPharmacyById}
                  showDetails={false}
                  showLevel={false}
                  showAddress={true}
                  showGroup={false}
                  fill={true}
                  allowEmpty={true}
                  renderNoItemsOnEmptyFilter
                />
                <InputGroup
                  placeholder="PlanningId"
                  onChange={(e) => {
                    this.setState({ searchPlanning: e.target.value });
                    this.searchDuties({
                      date: moment(this.state.searchDate)
                        .startOf('day')
                        .format('YYYY-MM-DD'),
                      notAssigned: false,
                      planningId: e.target.value,
                    });
                  }}
                />
              </>
            )}
          </div>
        </SearchContainer>
        {pharmacySelected && (
          <b>
            ({pharmacySelected!.level})&nbsp;&nbsp;{pharmacySelected!.apbNumber}&nbsp;-&nbsp;
            {pharmacySelected!.name}
            &nbsp;-&nbsp;{pharmacySelected!.address},&nbsp;
            {pharmacySelected!.zipCode}&nbsp;
            {pharmacySelected!.city}
          </b>
        )}
        <Maps>
          <MapContainer>
            <Label
              style={{
                display: 'flex',
                justifyContent: 'center',
                fontWeight: 'bold',
              }}
            >
              {isSecretariat && translate(TLCode.DUTYASSIGNMENT_DAY)}{' '}
              {isSecretariat && (
                <>
                  : {filteredDayDutyCount} {translate(TLCode.DUTYASSIGNMENT_TOTAL)} {filteredLvl1DayDutyCount}{' '}
                  {translate(TLCode.DUTYASSIGNMENT_LEVEL1)} {filteredLvl3DayDutyCount}{' '}
                  {translate(TLCode.DUTYASSIGNMENT_LEVEL3)}
                </>
              )}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {isSecretariat && translate(TLCode.DUTYASSIGNMENT_NIGHT)}{' '}
              {isSecretariat && (
                <>
                  : {filteredNightDutyCount} {translate(TLCode.DUTYASSIGNMENT_TOTAL)} {filteredLvl1NightDutyCount}{' '}
                  {translate(TLCode.DUTYASSIGNMENT_LEVEL1)} {filteredLvl3NightDutyCount}{' '}
                  {translate(TLCode.DUTYASSIGNMENT_LEVEL3)}
                </>
              )}
            </Label>
            <DivFlex>
              <DivColumn1 style={{ width: 85 }}>
                {!this.props.minimalDutiesPage && (
                  <>
                    <div style={{ background: '#FFCA18' }}>
                      <Checkbox
                        disabled={this.state.groupCheckboxesDisabled}
                        onChange={() => this.handleGroupToggle('zero')}
                        checked={this.state.groups.filter((group) => group.value == 'zero').length > 0}
                        style={{ marginLeft: '5px' }}
                      >
                        Niv0
                      </Checkbox>
                    </div>
                    <div style={{ background: '#FFCA18' }}>
                      <Checkbox
                        disabled={this.state.groupCheckboxesDisabled}
                        onChange={() => this.handleGroupToggle('other')}
                        checked={this.state.groups.filter((group) => group.value == 'other').length > 0}
                        style={{ marginLeft: '5px' }}
                      >
                        Autres
                      </Checkbox>
                    </div>
                    <div style={{ background: '#878080' }}>
                      <Checkbox
                        disabled={this.state.groupCheckboxesDisabled}
                        onChange={() => this.handleGroupToggle('close')}
                        checked={this.state.groups.filter((group) => group.value == 'close').length > 0}
                        style={{ marginLeft: '5px' }}
                      >
                        Proches
                      </Checkbox>
                    </div>
                    <div style={{ background: '#FFCA18' }}>
                      <Checkbox
                        disabled={this.state.groupCheckboxesDisabled}
                        onChange={() => this.handleGroupToggle('one')}
                        checked={this.state.groups.filter((group) => group.value == 'one').length > 0}
                        style={{ marginLeft: '5px' }}
                      >
                        Niv1
                      </Checkbox>
                    </div>
                  </>
                )}
                <>
                  {this.props.groupList &&
                    this.props
                      .groupList!.filter(
                        (g) => g.secretariatId == this.getSecretariatId() || !this.props.minimalDutiesPage,
                      )
                      .sort((a:any, b:any) => {
                        if (a.name < b.name) return-1;
                        if (a.name > b.name) return 1;
                        return 0;
                      })
                      .map((grp) => (
                        <div style={{ background: '#' + grp.color }} key={grp.description}>
                          <Tooltip content={grp.description} position={TTPosition.LEFT} hoverOpenDelay={800}>
                            <Checkbox
                              disabled={this.state.groupCheckboxesDisabled}
                              onChange={() => this.handleGroupToggle(grp.name)}
                              checked={this.state.groups.filter((group) => group.value == grp.name).length > 0}
                              style={{ marginLeft: '5px', marginRight: '10px' }}
                            >
                              {grp.name}
                            </Checkbox>
                          </Tooltip>
                        </div>
                      ))}
                  <br />
                  <br />
                  <div style={{ margin: '0 auto' }}>
                    <Button
                      intent="primary"
                      small={true}
                      style={{ height: '30px' }}
                      onClick={() => {
                        this.setState({
                          showPharmacyNotOnDuty: !this.state.showPharmacyNotOnDuty,
                          groupCheckboxesDisabled: !this.state.groupCheckboxesDisabled,
                        });
                      }}
                      text={'on/off'}
                    />
                  </div>
                  <div style={{ margin: '0 auto' }}>
                    <Button
                      intent="primary"
                      small={true}
                      style={{ height: '30px' }}
                      onClick={() => {
                        const lst = this.props
                          .groupList!.filter(
                            (g) => g.secretariatId == this.getSecretariatId() || !this.props.minimalDutiesPage,
                          )
                          .map((g) => {
                            return { label: g.name, value: g.name };
                          });
                        this.setState({ groups: lst });
                      }}
                      text={translate(TLCode.DUTIES_CheckAllGroups)}
                    />
                  </div>
                  <div style={{ margin: '0 auto' }}>
                    <Button
                      intent="primary"
                      small={true}
                      style={{ height: '30px' }}
                      onClick={() => {
                        this.setState({ groups: [] });
                      }}
                      text={translate(TLCode.DUTIES_UncheckAllGroups)}
                    />
                  </div>
                </>
              </DivColumn1>
              <PharmagardeMap
                center={centerCoordinates}
                onTilesLoaded={() => {
                  this._isMounted && this.setState({ initialZoomCalculatedDay: true });
                }}
                onLoad={(map: google.maps.Map) => {
                  this.setState({ map });
                }}
                gridSize={5}
                maximalZoom={18}
                minimalZoom={8}
                initialZoom={11}
              >
                {initialZoomCalculatedDay
                  ? this.memoizedRenderMarker(
                      dutiesDay,
                      dutiesNight,
                      'DAY',
                      this.state.groups,
                      pharmacySelected,
                      showDayDuties,
                      showNightDuties,
                      showPharmacyDutyCircle,
                      showPharmacyDuty30kmCircle,
                      showAnchor,
                      pharmacyNeighborsDistanceToShow,
                    )
                  : undefined}
              </PharmagardeMap>
              {this.props.minimalDutiesPage && this.props.pharmacyId ? (
                <DivColumn1 style={{ paddingLeft: 15, paddingTop: 30 }}>
                  <Slider
                    vertical={true}
                    onChange={(value: number) => {
                      this.onUpdateSlider(value);
                    }}
                    value={pharmacyNeighborsDistanceToShow / 1000}
                    min={1}
                    max={30}
                    stepSize={0.1}
                    labelStepSize={29}
                    labelRenderer={(value: number) => `${Math.round(value * 10) / 10}km`}
                  />
                </DivColumn1>
              ) : (
                <></>
              )}
            </DivFlex>
          </MapContainer>
        </Maps>
      </Container>
    );
  }
}

export default withTranslate(ShowDutiesOnWideMap);
